import React, { useEffect } from 'react';
import { Container, ThemeProvider } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';

import SpringHealthEnrollment from './SpringHealthEnrollment';
import { PageWrapper, ContentContainer } from './SpringHealthEnrollmentPage.styles';

import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import SpringHealthHeader from '@/components/SpringHealthHeader/SpringHealthHeader';
import SessionExpiredError from '@/components/SessionExpiredError/SessionExpiredError';
import { getTheme, Product, getProductSupportLink } from '@/utils/productHelpers';
import { getSpringHealthProgram } from '@/utils/posthog';
import { useSpringId } from '@/hooks/useSpringId';
import useSessionTimeout from '@/hooks/useSessionTimeout';
import { useConsent } from '@/hooks/useConsent';

type SpringHealthEnrollmentParams = {
  product: Product;
};

const ORG_SLUG = 'spring-health';

const SpringHealthEnrollmentPage = () => {
  const { product } = useParams<SpringHealthEnrollmentParams>();
  const { springId } = useSpringId();
  const { isExpired } = useSessionTimeout();
  const supportLink = getProductSupportLink(product);
  const posthog = usePostHog();
  useConsent();

  useEffect(() => {
    if (springId && !isExpired) {
      // Reset PostHog identity to ensure a fresh anonymous session.
      // This prevents a previous enrollment's identified user from being
      // associated with the current session's events.
      posthog?.reset();
      posthog?.capture('ActivationFlowStarted', {
        program: getSpringHealthProgram(product),
        spring_member_id: springId,
        org_slug: ORG_SLUG,
      });
    }
  }, [springId, isExpired, posthog, product, getSpringHealthProgram]);

  return (
    <ThemeProvider theme={getTheme(product)}>
      <PageWrapper>
        <SpringHealthHeader product={product} />
        <ContentContainer>
          <Container maxWidth="sm">
            {(!springId || isExpired)
              ? <SessionExpiredError product={product} />
              : <SpringHealthEnrollment product={product} />}
            <ConsentWidget />
          </Container>
          <Footer supportLink={supportLink} />
        </ContentContainer>
      </PageWrapper>
    </ThemeProvider>
  );
};

export default SpringHealthEnrollmentPage;
